import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { UsersThree, Plus, UploadSimple, Eye, EyeSlash, Trash, PencilSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", pan: "", aadhaar: "", dob: "", mobile: "", email: "", address: "", client_type: "individual", it_portal_user_id: "", it_portal_password: "", notes: "" };

export default function ITClients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  const fetchClients = async () => {
    try { setClients((await axios.get(`${API}/it/clients`)).data); } catch (e) { /* noop */ }
  };
  useEffect(() => { fetchClients(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setError(""); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c, it_portal_password: c.it_portal_password || "" }); setEditId(c.id); setError(""); setOpen(true); };

  const save = async () => {
    setBusy(true); setError("");
    try {
      if (editId) await axios.put(`${API}/it/clients/${editId}`, form);
      else await axios.post(`${API}/it/clients`, form);
      setOpen(false); fetchClients();
    } catch (e) { setError(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client ${c.name} (${c.pan})? Iske ITR filings bhi delete honge.`)) return;
    await axios.delete(`${API}/it/clients/${c.id}`);
    fetchClients();
  };

  const importJson = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${API}/it/clients/parse-json`, fd);
      const p = r.data;
      setForm({ ...EMPTY, name: p.name || "", pan: p.pan || "", aadhaar: p.aadhaar || "", dob: (p.dob || "").slice(0, 10), mobile: p.mobile || "", email: p.email || "", address: p.address || "" });
      setEditId(null); setOpen(true);
    } catch (err) { setError(err.response?.data?.detail || "JSON import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const filtered = clients.filter((c) => !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.pan?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4 sm:p-8" data-testid="it-clients-page">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <UsersThree size={30} weight="duotone" className="text-emerald-800" />
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Client Manager</h1>
        <div className="ml-auto flex gap-2">
          <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={importJson} data-testid="it-json-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="it-import-json-btn" className="border-emerald-700 text-emerald-800 hover:bg-emerald-50">
            <UploadSimple size={16} className="mr-1" /> {importing ? "Parsing…" : "Import JSON (Prefill)"}
          </Button>
          <Button onClick={openCreate} data-testid="it-add-client-btn" className="bg-emerald-800 hover:bg-emerald-900 text-white">
            <Plus size={16} className="mr-1" /> New Client
          </Button>
        </div>
      </div>

      {error && !open && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm mb-4" data-testid="it-clients-error">{error}</div>}
      <Input placeholder="Search by name / PAN…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="it-client-search" className="max-w-sm mb-4" />

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto sticky-table">
        <table className="w-full text-sm">
          <thead className="sticky-thead bg-emerald-900 text-white uppercase text-[11px] font-bold">
            <tr>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">PAN</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Contact</th>
              <th className="px-4 py-3 text-left">IT Portal Login</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No clients — start with "New Client" or "Import JSON"</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-slate-100" data-testid={`it-client-row-${c.pan}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{c.name}<div className="text-[10px] text-slate-400">{c.dob}</div></td>
                <td className="px-4 py-3 font-mono text-xs">{c.pan}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-bold bg-slate-100 px-2 py-1 rounded-sm uppercase">{c.client_type}</span></td>
                <td className="px-4 py-3 text-xs">{c.mobile}<div className="text-slate-400">{c.email}</div></td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-mono">{c.it_portal_user_id || "—"}</div>
                  {c.it_portal_password && (
                    <div className="flex items-center gap-1 font-mono">
                      <span data-testid={`it-pw-${c.pan}`}>{revealed[c.id] ? c.it_portal_password : "••••••"}</span>
                      <button onClick={() => setRevealed((r) => ({ ...r, [c.id]: !r[c.id] }))} className="text-slate-400 hover:text-slate-900" data-testid={`it-pw-reveal-${c.pan}`}>
                        {revealed[c.id] ? <EyeSlash size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(c)} className="text-slate-500 hover:text-slate-900 mr-3" data-testid={`it-edit-${c.pan}`}><PencilSimple size={17} /></button>
                  <button onClick={() => remove(c)} className="text-red-400 hover:text-red-700" data-testid={`it-delete-${c.pan}`}><Trash size={17} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Client" : "New Income Tax Client"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Full Name *</Label><Input data-testid="itc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>PAN *</Label><Input data-testid="itc-pan" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" maxLength={10} /></div>
            <div><Label>Client Type</Label>
              <select data-testid="itc-type" value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="individual">Individual</option><option value="huf">HUF</option><option value="firm">Partnership Firm</option>
                <option value="llp">LLP</option><option value="company">Company</option><option value="trust">Trust</option>
              </select>
            </div>
            <div><Label>Aadhaar</Label><Input data-testid="itc-aadhaar" value={form.aadhaar || ""} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} /></div>
            <div><Label>Date of Birth</Label><Input type="date" data-testid="itc-dob" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></div>
            <div><Label>Mobile</Label><Input data-testid="itc-mobile" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" data-testid="itc-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input data-testid="itc-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
              <p className="text-xs font-bold text-emerald-800 mb-2">IT Portal Credentials (stored encrypted)</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Portal User ID</Label><Input data-testid="itc-portal-user" value={form.it_portal_user_id || ""} onChange={(e) => setForm({ ...form, it_portal_user_id: e.target.value })} placeholder="Usually PAN" /></div>
                <div><Label>Portal Password</Label><Input data-testid="itc-portal-pass" value={form.it_portal_password || ""} onChange={(e) => setForm({ ...form, it_portal_password: e.target.value })} /></div>
              </div>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Input data-testid="itc-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm" data-testid="itc-error">{error}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name || !form.pan} data-testid="itc-save-btn" className="bg-emerald-800 hover:bg-emerald-900 text-white">{busy ? "Saving…" : "Save Client"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
